<script lang="ts" setup>
import type { Product } from '~/models';

import ProductList from './ProductList.vue';
import ProductListMobile from './ProductListMobile.vue';

import { useNuxtApp } from '#app';

const props = defineProps<{
  modelValue: boolean;
  title: string;
  active: Product | null;
  products: Product[];
}>();

const emits = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'update:active', product: Product): void;
}>();

const { $device: { isMobile } } = useNuxtApp();

const onUpdateModelValue = (value: boolean): void => {
  emits('update:modelValue', value);
};

const onUpdateActive = (product: Product): void => {
  emits('update:active', product);
};
</script>

<template>
  <UModal
    v-if="isMobile"
    :model-value="props.modelValue"
    prevent-close
    fullscreen
    @update:model-value="onUpdateModelValue"
  >
    <ProductListMobile
      :title="props.title"
      :active="props.active"
      :products="props.products"
      @back="onUpdateModelValue(false)"
      @update:active="onUpdateActive"
    >
      <template #caption>
        <slot name="caption" />
      </template>

      <slot />
    </ProductListMobile>
  </UModal>

  <ProductList
    v-else
    :active="props.active"
    :products="props.products"
    data-test="list"
    @update:active="onUpdateActive"
  >
    <template #header>
      <div class="bg-card rounded-t-lg px-2 flex flex-col font-semibold items-center">
        <span class="px-2 py-4 w-full uppercase">{{ props.title }}</span>
        <slot name="caption" />
      </div>
    </template>

    <slot />
  </ProductList>
</template>

<style lang="scss" scoped>
.product-list-wrapper {
  &__header {
    position: fixed;
    width: 100%;
    padding-block: 0.5rem;
    display: grid;
    grid-template-columns: 2.25rem 1fr 2.25rem;
    align-items: center;

    text-transform: uppercase;
    font-weight: 600;
    line-height: normal;
    text-align: center;
  }
}
</style>
